import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import AutoFundraiserLogo from "./autofundraiser/AutoFundraiserLogo";
import "./autofundraiser/autofundraiser.css";

const AUTH_API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;
const TOKEN_KEY = "autoFundraiserSession";

function AuthShell({ children }) {
  return <div className="af-app">
    <header className="af-header"><a className="af-logo af-logo-compact" href="/"><AutoFundraiserLogo compact /></a></header>
    <main className="af-shell af-shell-narrow">{children}</main>
  </div>;
}

export function AccountSetupPage() {
  const navigate = useNavigate();
  const setupQuery = new URLSearchParams(useLocation().search);
  const token = setupQuery.get("token") || "";
  const gameReturn = setupQuery.get("return_to") === "game";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (password.length < 8 || password !== confirm) return;
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${AUTH_API}/setup`, { resume_token: token, password });
      if (data.account_exists) {
        const email = encodeURIComponent(data.email || "");
        navigate(`/login?resume_token=${encodeURIComponent(token)}&email=${email}${gameReturn ? "&return_to=game" : ""}`, { replace: true });
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.access_token);
      navigate((data.strategy_experience === "unified" || data.entry_source === "fundraising_game") || gameReturn ? `/strategy-development/workspace/${data.resume_token}` : `/strategy/${data.resume_token}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "We could not create your login.");
      setBusy(false);
    }
  };

  return <AuthShell><section className="af-work-card af-question-work">
    <div className="af-kicker">YOUR FUNDRAISING ACCOUNT</div>
    <h1>Create your login.</h1>
    <p>You've taken an important step towards building a fundraising direction your organization can confidently work with. Create your login so we can keep your answers together and help you pick up exactly where you leave off.</p>
    <p>Next, let's bring together what we know about your mission and the money your organization needs. Your answers will help shape the strategy for your organization, using the service you selected.</p>
    <div className="af-form-stack">
      <label>Create password<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" /></label>
      <label>Confirm password<input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></label>
    </div>
    {password && password.length < 8 && <p className="af-error">Use at least 8 characters.</p>}
    {confirm && password !== confirm && <p className="af-error">The passwords do not match.</p>}
    {error && <p className="af-error">{error}</p>}
    <button className="af-primary af-primary-wide" disabled={busy || password.length < 8 || password !== confirm || !token} onClick={submit}>{busy ? "CREATING ACCOUNT…" : "CREATE MY LOGIN"}</button>
  </section></AuthShell>;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const returnToken = query.get("resume_token") || "";
  const emailFromLink = query.get("email") || "";
  const [form, setForm] = useState({ email: emailFromLink, password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${AUTH_API}/login`, form);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      const destination = returnToken || data.resume_token;
      const returnTo = ["workspace", "execution", "strategy", "booking", "game"].includes(query.get("return_to")) ? query.get("return_to") : "strategy";
      const session = query.get("professional_session") ? `?professional_session=${encodeURIComponent(query.get("professional_session"))}` : returnTo === "execution" && query.get("execution_session") ? `?session_id=${encodeURIComponent(query.get("execution_session"))}` : "";
      navigate(destination ? returnTo === "game" || (!query.has("return_to") && (data.strategy_experience === "unified" || data.entry_source === "fundraising_game")) ? `/strategy-development/workspace/${destination}` : returnTo === "booking" ? `/book-call?resume_token=${encodeURIComponent(destination)}` : `/${returnTo}/${destination}${session}` : "/app", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "We could not log you in.");
      setBusy(false);
    }
  };

  return <AuthShell><section className="af-work-card af-question-work">
    <div className="af-kicker">WELCOME BACK</div>
    <h1>Log in to your fundraising workspace.</h1>
    <p>Your saved work is here, so you can continue from where you left off and keep developing your organization's fundraising direction.</p>
    <div className="af-form-stack">
      <label>Email<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
      <label>Password<input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit()} /></label>
    </div>
    {error && <p className="af-error">{error}</p>}
    <button className="af-primary af-primary-wide" disabled={busy || !form.email || !form.password} onClick={submit}>{busy ? "LOGGING IN…" : "LOG IN"}</button>
  </section></AuthShell>;
}

export function AppHomePage() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  useEffect(() => {
    const session = localStorage.getItem(TOKEN_KEY);
    if (!session) { navigate("/login", { replace: true }); return; }
    axios.get(`${AUTH_API}/me`, { headers: { Authorization: `Bearer ${session}` } })
      .then(({ data }) => {
        if (data.resume_token) navigate((data.strategy_experience === "unified" || data.entry_source === "fundraising_game") ? `/strategy-development/workspace/${data.resume_token}` : `/workspace/${data.resume_token}`, { replace: true });
        else setError("We could not find a paid strategy attached to this account.");
      })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); navigate("/login", { replace: true }); });
  }, [navigate]);
  return <AuthShell>{error ? <p className="af-error">{error}</p> : <div className="af-thinking"><h2>Opening your fundraising workspace…</h2></div>}</AuthShell>;
}
